import React from "react";
import { Flame, Sparkles, Radio, TrendingUp } from "lucide-react";
import useAegisStream from "../../hooks/useAegisStream";

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return Math.round(x).toLocaleString();
}

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5) return "now";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function EventChip({ evt }) {
  const isBurn = evt.type === "burn";
  const isMint = evt.type === "mint";
  const Icon = isBurn ? Flame : Sparkles;
  const color = isBurn
    ? "border-rose-500/40 bg-rose-500/5 text-rose-200"
    : "border-amber-500/40 bg-amber-500/5 text-amber-200";
  const label = isBurn ? "BURN" : isMint ? "MINT" : evt.type.toUpperCase();
  const amount = isBurn
    ? evt?.data?.amount_proof
    : evt?.data?.value_in_proof;
  return (
    <span
      data-testid={`ticker-event-${evt.type}`}
      className={`inline-flex items-center gap-1.5 border ${color} px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-mono whitespace-nowrap`}
      title={isBurn ? evt?.data?.reason : `discoverer · ${evt?.data?.recipient_user_id || "?"}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{label}</span>
      <span className="text-zinc-100">{formatProof(amount)}</span>
      <span className="text-zinc-500">{isBurn ? "PROOF burned" : "PROOF"}</span>
      <span className="text-zinc-600">· {relTime(evt.at)}</span>
    </span>
  );
}

/**
 * Slim live ticker bar — Server-Sent Events powered. Embed at the top
 * of public pages (Leaderboard, Wallet, Pricing) so the deflationary
 * mechanism feels alive.
 */
export default function LiveTicker({ fallbackMultiplier = null }) {
  const { events, multiplier, connected } = useAegisStream(8);
  const visible = events.filter((e) => e.type === "mint" || e.type === "burn");
  const liveMult = multiplier != null ? multiplier : fallbackMultiplier;
  return (
    <div
      data-testid="aegis-live-ticker"
      className="relative border-y border-amber-500/20 bg-[#0a0a0c] overflow-hidden"
    >
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a0a0c] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a0a0c] to-transparent z-10 pointer-events-none" />
      <div className="flex items-center gap-3 px-3 py-2">
        <div
          className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono"
          data-testid="ticker-status"
          title={connected ? "Connected to live SSE stream" : "Reconnecting…"}
        >
          <Radio
            className={`h-3 w-3 ${
              connected ? "text-emerald-400 animate-pulse" : "text-zinc-600"
            }`}
          />
          <span className={connected ? "text-emerald-400" : "text-zinc-600"}>
            {connected ? "live" : "…"}
          </span>
        </div>

        {liveMult != null && (
          <div
            data-testid="ticker-multiplier"
            className="shrink-0 inline-flex items-center gap-1.5 border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-mono"
            title="Burn multiplier — implied value uplift on every coin from supply destruction"
          >
            <TrendingUp className="h-3 w-3" />
            <span>×{Number(liveMult).toFixed(liveMult < 10 ? 4 : 2)}</span>
            <span className="text-zinc-500">multiplier</span>
          </div>
        )}

        <div className="flex-1 min-w-0 overflow-hidden">
          {visible.length === 0 ? (
            <div
              className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-mono"
              data-testid="ticker-empty"
            >
              waiting for next mint or burn…
            </div>
          ) : (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              {visible.map((e, i) => (
                <EventChip key={`${e.at}-${i}`} evt={e} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
